
import { supabase } from "@/integrations/supabase/client";
import Cookies from "js-cookie";
import { 
  REFERRAL_COOKIE_NAME, 
  AFFILIATE_COOKIE_NAME,
  getReferralCodeFromCookie,
  getAffiliateIdFromCookie
} from "@/hooks/referrals/referralUtils";

export const processReferral = async (userId: string): Promise<void> => {
  try {
    // Check for referral code in cookie
    const referralCode = getReferralCodeFromCookie();
    const affiliateId = getAffiliateIdFromCookie();
    
    console.log("Processing referral for user:", userId);
    console.log("Referral cookie value:", referralCode);
    console.log("Affiliate cookie value:", affiliateId);
    
    // Process referral relationship if a referral code exists
    if (referralCode) {
      console.log("Found referral code in cookie:", referralCode);
      
      // Find the referrer's user ID from the code
      const { data: referralData, error: referralError } = await supabase
        .from('referral_codes')
        .select('user_id')
        .eq('code', referralCode)
        .single();
        
      if (referralError && referralError.code !== 'PGRST116') {
        // Don't throw an error here, just log it - this shouldn't block signup
        console.error("Error finding referrer:", referralError);
      } else if (referralData && referralData.user_id) {
        // Verify the user is authenticated before creating relationship
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error("No active session when trying to create referral relationship");
        } else {
          // Create the referral relationship 
          const { error: relationshipError } = await supabase
            .from('referral_relationships')
            .insert({
              referrer_id: referralData.user_id,
              referred_id: userId
            });
            
          if (relationshipError) {
            console.error("Error creating referral relationship:", relationshipError);
          } else {
            console.log("Referral relationship created successfully");
            
            // Process reward for the referrer
            await processReferrerRewards(referralData.user_id);
            
            // Check if the referrer has an affiliated_by value (key change for inheritance)
            const { data: referrerData, error: referrerError } = await supabase
              .from('user_roles')
              .select('affiliated_by')
              .eq('user_id', referralData.user_id)
              .single();
              
            if (!referrerError && referrerData && referrerData.affiliated_by) {
              // Inherit the affiliate ID from the referrer - this ensures the chain is preserved
              await updateUserAffiliation(userId, referrerData.affiliated_by);
              console.log("Inherited affiliate ID from referrer:", referrerData.affiliated_by);
            }
          }
        }
      }
      
      // Clear the referral cookie after processing
      try {
        Cookies.remove(REFERRAL_COOKIE_NAME);
        console.log("Removed referral cookie after processing");
      } catch (cookieError) {
        console.error("Error removing referral cookie:", cookieError);
      }
    }
    
    // Process affiliate ID if it exists - this takes precedence over inherited affiliation
    if (affiliateId) {
      console.log("Found affiliate ID in cookie:", affiliateId);
      
      // Update the user with the affiliate ID
      const result = await updateUserAffiliation(userId, affiliateId);
      
      if (result) {
        // Clear the affiliate cookie after successful processing
        try {
          Cookies.remove(AFFILIATE_COOKIE_NAME);
          console.log("Removed affiliate cookie after processing");
        } catch (cookieError) {
          console.error("Error removing affiliate cookie:", cookieError);
        }
      }
    }
  } catch (error) {
    console.error("Error processing referral/affiliation:", error);
    // Don't block signup if processing fails
  }
};

// Helper function to update user affiliation
export const updateUserAffiliation = async (userId: string, affiliateId: string): Promise<boolean> => {
  try {
    console.log(`Attempting to update user ${userId} affiliation with ${affiliateId}`);
    
    // Initial delay to allow database triggers to complete
    const initialWait = 1500; // 1.5 seconds
    console.log(`Waiting ${initialWait}ms before first attempt to allow database triggers to complete...`);
    await new Promise(resolve => setTimeout(resolve, initialWait));
    
    // Implement exponential backoff for retrying
    const maxRetries = 5;
    const initialDelay = 1000; // 1 second between retries
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Wait longer between retries (exponential backoff)
        if (attempt > 0) {
          const delay = initialDelay * Math.pow(2, attempt - 1);
          console.log(`Retry attempt ${attempt}: waiting ${delay}ms before trying again`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        
        // Check if the user_roles record exists
        const { data: existingRole, error: roleCheckError } = await supabase
          .from('user_roles')
          .select('id')
          .eq('user_id', userId)
          .single();
          
        if (roleCheckError) {
          console.warn(`User role check failed (attempt ${attempt + 1}/${maxRetries + 1}):`, roleCheckError);
          if (attempt < maxRetries) continue;
          throw roleCheckError;
        }
        
        if (!existingRole) {
          console.warn(`No user_roles record found yet for user ${userId} (attempt ${attempt + 1}/${maxRetries + 1})`);
          if (attempt < maxRetries) continue;
          throw new Error("User roles record not found after maximum retries");
        }
        
        console.log(`Found user_roles record for ${userId}, proceeding with update...`);
        
        // Now update the record
        const { data, error } = await supabase
          .from('user_roles')
          .update({ affiliated_by: affiliateId })
          .eq('user_id', userId)
          .select();
          
        if (error) {
          console.error(`Error updating user affiliation (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
          if (attempt < maxRetries) continue;
          throw error;
        }
        
        // Check if any rows were actually affected
        if (!data || data.length === 0) {
          console.warn(`Update query succeeded but no rows were affected (attempt ${attempt + 1}/${maxRetries + 1})`);
          
          // If we've tried multiple times and still no success, use the direct method
          if (attempt >= 2) {
            console.log("Trying direct method via debug-referrals function...");
            
            // Get the current user session for the auth token
            const { data: { session } } = await supabase.auth.getSession();
            const authHeader = session 
              ? { Authorization: `Bearer ${session.access_token}` }
              : undefined;
              
            const directResult = await supabase.functions.invoke('debug-referrals', {
              body: { 
                userId, 
                affiliateId, 
                action: "setAffiliation" 
              },
              // Include auth header if we have a session
              headers: authHeader
            });
            
            if (directResult.error) {
              console.error("Direct affiliation attempt failed:", directResult.error);
              if (attempt < maxRetries) continue;
              throw new Error(`Direct affiliation failed: ${directResult.error.message}`);
            }
            
            console.log("Direct affiliation result:", directResult.data);
            if (directResult.data?.directAction?.success) {
              console.log(`Successfully set affiliation using direct method for user ${userId}`);
              return true;
            }
          }
          
          if (attempt < maxRetries) continue;
          throw new Error("Update succeeded but no rows were affected");
        }
        
        console.log(`User ${userId} successfully affiliated with ${affiliateId}`);
        return true;
      } catch (retryError) {
        if (attempt < maxRetries) {
          console.warn(`Exception during affiliation update (attempt ${attempt + 1}/${maxRetries + 1}):`, retryError);
          continue;
        }
        throw retryError;
      }
    }
    
    // If we reach here, all retries failed
    console.error(`Failed to update affiliated_by after ${maxRetries + 1} attempts`);
    return false;
  } catch (error) {
    console.error("Error in updateUserAffiliation after all retries:", error);
    return false;
  }
};

export const processReferrerRewards = async (referrerId: string): Promise<void> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      console.error("No session available to process referrer rewards");
      return;
    }
    
    const response = await supabase.functions.invoke('process-referral-reward', {
      body: { userId: referrerId },
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    if (response.error) {
      console.error("Error processing referrer rewards:", response.error);
    } else {
      console.log("Referrer rewards processed:", response.data);
    }
  } catch (rewardError) {
    console.error("Error processing referrer rewards:", rewardError);
  }
};
